import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, styled } from '@mui/material'
import { Skeleton } from '@mui/material'
import { TreeView } from '@mui/x-tree-view/TreeView'
import { ReactComponent as PlusSquareIconSvg } from './Resources/collapse.svg'
import { ReactComponent as MinusquareIconSvg } from './Resources/constrain.svg'
import { isNilOrEmpty } from '../../core'
import config from 'config'
import Scrollbars from 'react-custom-scrollbars'
import TreeItem from './TreeItem'

const getFullImageUrl = (imagePath) => `${config.BASE_EID_URL}${imagePath}`

const StyledTree = styled(TreeView)({
    height: '100%',
    flexGrow: 1,
})

const extractAllNodeIdsFromTree = (tree) => {
    let ids = []

    const traverse = (node) => {
        ids.push(node.id)
        if (node.children && node.children.length > 0) {
            node.children.forEach((child) => traverse(child))
        }
    }

    tree.forEach((rootNode) => traverse(rootNode))

    return ids
}

export default function Tree({
    data,
    handleSelect,
    loading,
    selectedItem,
    searchTerm,
    idProp = 'id',
    labelProp = 'friendlyName',
    ...rest
}) {
    const [expanded, setExpanded] = useState([])
    const [treeItems, setTreeItems] = useState([])
    const [pageNo, setPageNo] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const scrollbarsRef = useRef(null)
    const treeItemRefs = useRef({})

    useEffect(() => {
        if (!isNilOrEmpty(searchTerm)) {
            setExpanded(extractAllNodeIdsFromTree(data))
        }
    }, [data, searchTerm])

    const onNodeToggle = (event, nodeIds) => {
        setExpanded(nodeIds)
    }

    // Find node by ID in the tree data
    const findNodeById = useCallback(
        (nodeId) => {
            const traverse = (nodes) => {
                for (const node of nodes) {
                    if (node[idProp] === nodeId) {
                        return node
                    }
                    if (node.children && node.children.length > 0) {
                        const found = traverse(node.children)
                        if (found) return found
                    }
                }
                return null
            }
            return traverse(treeItems || [])
        },
        [treeItems, idProp],
    )

    // Extract nodeId from aria-activedescendant
    const extractNodeIdFromActiveDescendant = useCallback(
        (activeDescendantId) => {
            if (!activeDescendantId) return null
            // Format is usually like ":r1t:-<nodeId>" so we need to extract the nodeId part
            const parts = activeDescendantId.split('-')
            // Remove the first part (the TreeView ID prefix) and rejoin with dashes
            return parts.slice(1).join('-')
        },
        [],
    )

    // Auto-scroll focused item into view
    const scrollToFocusedItem = useCallback((nodeId) => {
        const element = treeItemRefs.current[nodeId]
        if (!element) {
            return
        }

        try {
            // Get the TreeItem content element (the actual focusable element)
            const contentElement = element.querySelector('.MuiTreeItem-content')
            if (!contentElement) {
                return
            }

            // Check if element is in viewport
            const rect = contentElement.getBoundingClientRect()
            const scrollContainer = scrollbarsRef.current?.view

            if (!scrollContainer) {
                return
            }

            const containerRect = scrollContainer.getBoundingClientRect()

            // Only scroll if element is not fully visible
            const isVisible =
                rect.top >= containerRect.top &&
                rect.bottom <= containerRect.bottom

            if (!isVisible) {
                // Use scrollIntoView with block: 'nearest' for minimal scrolling
                contentElement.scrollIntoView({
                    behavior: 'auto', // Use auto instead of smooth to prevent compounding
                    block: 'nearest',
                    inline: 'nearest',
                })
            }
        } catch (error) {
            // Silently fail if there's an error
            console.warn('Error scrolling to focused item:', error)
        }
    }, [])

    // Handle keyboard events on the TreeView
    const handleTreeKeyDown = useCallback(
        (event) => {
            const treeElement = event.currentTarget
            const activeDescendantId = treeElement.getAttribute(
                'aria-activedescendant',
            )

            // Handle navigation keys - scroll the focused item into view
            if (
                event.key === 'ArrowUp' ||
                event.key === 'ArrowDown' ||
                event.key === 'ArrowLeft' ||
                event.key === 'ArrowRight'
            ) {
                // Wait for MUI to update aria-activedescendant, then scroll
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        const updatedActiveDescendantId =
                            treeElement.getAttribute('aria-activedescendant')
                        if (updatedActiveDescendantId) {
                            const nodeId = extractNodeIdFromActiveDescendant(
                                updatedActiveDescendantId,
                            )
                            if (nodeId) {
                                scrollToFocusedItem(nodeId)
                            }
                        }
                    })
                })
            }

            // Handle selection keys
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()

                if (activeDescendantId) {
                    const nodeId =
                        extractNodeIdFromActiveDescendant(activeDescendantId)

                    if (nodeId) {
                        const node = findNodeById(nodeId)
                        if (node) {
                            handleSelect(node)
                        }
                    }
                }
            }
        },
        [
            findNodeById,
            handleSelect,
            extractNodeIdFromActiveDescendant,
            scrollToFocusedItem,
        ],
    )

    useEffect(() => {
        if (data?.length) {
            let tempList = [...data]
            // NOTE: First chunk to load first 50 records
            if (tempList.length > 50) {
                tempList.length = 50
                setHasMore(true)
            }
            setTreeItems(tempList)
            setPageNo(1)
        } else {
            setTreeItems([])
            setPageNo(1)
        }
    }, [data])

    const renderRootNodes = (node) => {
        const isParentSelected =
            selectedItem && selectedItem[idProp] === node[idProp]
        return (
            <TreeItem
                ref={(el) => {
                    if (el) {
                        treeItemRefs.current[node[idProp]] = el
                    }
                }}
                key={node[idProp]}
                nodeId={node[idProp]}
                isRoot={true}
                label={node[labelProp]}
                count={
                    node.children.length > 0 ? node.children.length : undefined
                }
                highlightText={searchTerm}
                selected={selectedItem && selectedItem[idProp] === node[idProp]}
                onClick={(event) => {
                    event.preventDefault()
                    handleSelect(node)
                }}
                iconUrl={
                    node.iconUrl && node.iconUrl.length > 0
                        ? getFullImageUrl(node.iconUrl)
                        : null
                }
                {...rest}
            >
                {Array.isArray(node.children) &&
                    node.children.map((currentNode) =>
                        renderSubNodes(currentNode, isParentSelected),
                    )}
            </TreeItem>
        )
    }

    const renderSubNodes = (node, isParentSelected) => {
        const _isParentSelected = isParentSelected
            ? isParentSelected
            : selectedItem && selectedItem[idProp] === node[idProp]
        return (
            <TreeItem
                ref={(el) => {
                    if (el) {
                        treeItemRefs.current[node[idProp]] = el
                    }
                }}
                key={node[idProp]}
                nodeId={node[idProp]}
                label={node[labelProp]}
                count={
                    node.children.length > 0 ? node.children.length : undefined
                }
                highlightText={searchTerm}
                selected={selectedItem && selectedItem[idProp] === node[idProp]}
                onClick={(event) => {
                    event && event.preventDefault()
                    handleSelect(node)
                }}
                iconUrl={
                    node.iconUrl && node.iconUrl.length > 0
                        ? getFullImageUrl(node.iconUrl)
                        : null
                }
                isParentSelected={isParentSelected}
                {...rest}
            >
                {Array.isArray(node.children) &&
                    node.children.map((currentNode) =>
                        renderSubNodes(currentNode, _isParentSelected),
                    )}
            </TreeItem>
        )
    }

    const bindMoreData = (response) => {
        if (response.top >= 0.99 && hasMore) {
            let tempList = [...data]
            const startIndex = pageNo * 50
            const endIndex = (pageNo + 1) * 50
            const refinedList = tempList.slice(startIndex, endIndex)
            const combinedList = [...treeItems, ...refinedList]
            setTreeItems(combinedList)
            setPageNo(pageNo + 1)
            if (endIndex >= data.length) {
                setHasMore(false)
            }
        }
    }

    return loading ? (
        <>
            <Box margin="4px 8px">
                <Skeleton animation="wave" variant="rectangular" height={20} />
            </Box>
            <Box margin="4px 8px">
                <Skeleton animation="wave" variant="rectangular" height={20} />
            </Box>
        </>
    ) : (
        data && (
            <StyledTree
                defaultCollapseIcon={<MinusquareIconSvg />}
                defaultExpandIcon={<PlusSquareIconSvg />}
                expanded={expanded?.length < 5000 ? expanded : []}
                onNodeToggle={onNodeToggle}
                onKeyDown={handleTreeKeyDown}
                disableSelection
            >
                <div
                    style={{
                        maxHeight: 300,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                    }}
                >
                    <Scrollbars
                        ref={scrollbarsRef}
                        className="scrollPriceChange"
                        onScrollFrame={bindMoreData}
                        autoHide
                        style={{ height: '80vh' }}
                    >
                        {treeItems.map((item) => (
                            <React.Fragment key={item[idProp]}>
                                {renderRootNodes(item)}
                            </React.Fragment>
                        ))}
                    </Scrollbars>
                </div>
            </StyledTree>
        )
    )
}

Tree.propTypes = {
    data: PropTypes.array,
    handleSelect: PropTypes.func.isRequired,
}

Tree.defaultProps = {}
